import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { I, CAT_ICON, toolIcon } from './Icons';

const API = process.env.REACT_APP_BACKEND_URL;
const OPEN_KEY = 'derwent.sidebar.openGroups';

// Sidebar — a 1:1 port of the prototype's <aside class="side">, fed by the real tool registry.
export const DwSidebar = ({ user, favTools, groups, act, onNav, onLaunch, onLogout, open, onClose, token, onEditFav }) => {
  const initials = (user?.name || 'U').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const role = (user?.role || 'user').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const [openGroups, setOpenGroups] = useState(() => { try { return new Set(JSON.parse(localStorage.getItem(OPEN_KEY) || '[]')); } catch { return new Set(); } });
  useEffect(() => { try { localStorage.setItem(OPEN_KEY, JSON.stringify([...openGroups])); } catch { /* ignore */ } }, [openGroups]);
  const toggle = (id) => setOpenGroups((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [health, setHealth] = useState(null);
  useEffect(() => {
    if (!token) return undefined;
    let on = true;
    const load = () => axios.get(`${API}/api/diagnostics/overview`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => on && setHealth(r.data)).catch(() => {});
    load(); const t = setInterval(load, 60000);
    return () => { on = false; clearInterval(t); };
  }, [token]);
  const ok = !health || health.overall?.tone === 'operational';
  const dbOk = !health || health.database?.status === 'operational';
  const Row = ({ label, good }) => (
    <div className={`sys-row${good ? '' : ' warn'}`}><span className={`ld${good ? '' : ' amber'}`} />{label}<span className={`mono${good ? '' : ' warn'}`}>{good ? 'LIVE' : 'DEGRADED'}</span></div>
  );
  const nav = (e, fn) => { e.preventDefault(); onClose?.(); fn(); };

  return (
    <aside className={`side${open ? ' on' : ''}`} aria-label="Main navigation" data-testid="dw-sidebar">
      <div className="brand">
        <a className="logo" href="/" onClick={(e) => nav(e, () => onNav('/'))} aria-label="DerwentAI home" data-testid="brand-logo"><img src="/derwentai-logo.png" alt="DerwentAI" className="logo-img" draggable={false} /></a>
        <span className="chip" data-testid="dw-role-chip">{user?.is_master ? 'Master' : role}</span>
      </div>

      <nav className="nav-group">
        <a className={`nav${act === 'home' ? ' active' : ''}`} href="/" aria-current={act === 'home' ? 'page' : undefined} onClick={(e) => nav(e, () => onNav('/'))} data-testid="nav-home"><I id="home" /><span className="lbl">Home</span></a>
        <a className={`nav${act === 'myweek' ? ' active' : ''}`} href="/myweek" onClick={(e) => nav(e, () => onNav('/myweek'))} data-testid="nav-intelligence"><I id="cal" /><span className="lbl">MyWeek</span></a>
        {(user?.is_master || user?.role === 'admin') && <a className="nav" href="/system-diagnostics" onClick={(e) => nav(e, () => onNav('/system-diagnostics'))} data-testid="nav-system-diagnostics"><I id="pulse" /><span className="lbl">System Diagnostics</span></a>}
        {user?.role === 'broker_support' && <a className="nav" href="/my-activity" onClick={(e) => nav(e, () => onNav('/my-activity'))} data-testid="nav-my-activity"><I id="clock" /><span className="lbl">My Activity</span></a>}
      </nav>

      {(favTools.length > 0 || onEditFav) && (
        <nav className="nav-group" aria-label="Favourites">
          <div className="nav-label nav-label-row">Favourites{onEditFav && <button type="button" className="nav-edit" onClick={onEditFav} data-testid="sidebar-fav-edit">{favTools.length ? 'Edit' : '+ Add'}</button>}</div>
          {favTools.map((t) => (
            <a key={t.key} className={`nav${act === t.key ? ' active' : ''}`} href={t.path} onClick={(e) => nav(e, () => onLaunch(t))} data-testid={`sidebar-fav-${t.key}`}><I id={toolIcon(t)} /><span className="lbl">{t.title}</span></a>
          ))}
        </nav>
      )}

      <nav className="nav-group" aria-label="Workspace">
        <div className="nav-label">Workspace</div>
        {groups.map(({ cat, items }) => (
          <React.Fragment key={cat.id}>
            <a className={`nav${openGroups.has(cat.id) ? ' open' : ''}`} href={`#${cat.id}`} onClick={(e) => { e.preventDefault(); toggle(cat.id); }} aria-expanded={openGroups.has(cat.id)} data-testid={`sidebar-group-${cat.id}`}>
              <I id={CAT_ICON[cat.id] || 'grid'} /><span className="lbl">{cat.label}</span><span className="count">{items.length}</span><I id="chev" size={14} className="chev" />
            </a>
            {openGroups.has(cat.id) && items.map((t) => (
              <a key={t.key} className={`nav sub${act === t.key ? ' active' : ''}`} href={t.path} onClick={(e) => nav(e, () => onLaunch(t))} data-testid={`sidebar-tool-${t.key}`}><span className="lbl">{t.title}</span></a>
            ))}
          </React.Fragment>
        ))}
      </nav>

      <div className="side-foot">
        <div className="sys" data-testid="dw-systems">
          <div className="sys-head">Systems<a href="/system-diagnostics" onClick={(e) => nav(e, () => onNav('/system-diagnostics'))}>Diagnostics</a></div>
          <Row label="Game Plan Tracker" good={ok} />
          <Row label="Client Portals" good={ok} />
          <Row label="Lead Nurture" good={ok} />
          <Row label="Database" good={dbOk} />
        </div>
        <div className="me" data-testid="sidebar-user">
          {user?.avatar_url ? <img src={user.avatar_url} alt="" className="avatar" style={{ objectFit: 'cover' }} /> : <div className="avatar">{initials}</div>}
          <div style={{ minWidth: 0 }}><div className="me-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div><div className="me-role">{role} · Hobart</div></div>
          <button type="button" className="icon-btn" style={{ marginLeft: 'auto' }} aria-label="Sign out" onClick={onLogout} data-testid="sidebar-logout"><I id="out" /></button>
        </div>
      </div>
    </aside>
  );
};

// Top bar — clock, command bar, Create, theme, notifications, shortcuts.
export const DwTopBar = ({ clock, onPalette, onTheme, theme, onKeys, onMenu, bell }) => (
  <header className="top" data-testid="dw-topbar">
    <button type="button" className="icon-btn menu-btn" aria-label="Open menu" onClick={onMenu} data-testid="dw-menu"><I id="menu" /></button>
    <div className="clock" data-testid="home-clock"><span className="ld" /><b>{clock || '--:--:--'}</b><span>HOBART</span></div>
    <button type="button" className="search" aria-label="Search or run a command" onClick={onPalette} data-testid="dw-search">
      <I id="search" /><span className="grow">Search clients, tools and commands</span><kbd>{`${/Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl'}K`}</kbd>
    </button>
    <button type="button" className="btn btn-accent hide-sm" onClick={onPalette} data-testid="dw-create"><I id="plus" size={16} sw={2.2} />Create</button>
    <button type="button" className="icon-btn" aria-label="Toggle dark mode" onClick={onTheme} data-testid="home-theme-toggle"><I id={theme === 'dark' ? 'sun' : 'moon'} /></button>
    <span className="bell-wrap" data-testid="dw-bell">{bell}</span>
    <button type="button" className="icon-btn hide-sm" aria-label="Keyboard shortcuts" onClick={onKeys} data-testid="dw-keys"><I id="key" /></button>
  </header>
);
